import {
  Card as HeroUICard,
  CardBody as HeroUICardBody,
  CardFooter as HeroUICardFooter,
  CardHeader as HeroUICardHeader,
  CardProps as HeroUICardProps,
} from "@heroui/react";
import { TourElement } from "core/types";
import { generateTrackName } from "core/validationSchemas/utils";
import { ButtonProps, LinkProps } from "ds/ui";
import { useTracking } from "react-tracking";

type BaseCardProps = {
  id: string;
  trackEventProps?: ButtonProps["trackEventProps"];
  tour?: TourElement;
} & Omit<HeroUICardProps, "onPress" | "as" | "isPressable" | "id">;

type ButtonCardProps = BaseCardProps & {
  as: "button";
  isLoading?: ButtonProps["isLoading"];
  onPress: ButtonProps["onPress"];
};

type LinkCardProps = BaseCardProps & {
  as: "a";
  href: LinkProps["href"];
  onPress?: LinkProps["onPress"];
};

type StaticCardProps = BaseCardProps & {
  as?: Exclude<HeroUICardProps["as"], "button" | "a">;
  onPress?: HeroUICardProps["onPress"];
};

type CardProps = StaticCardProps | ButtonCardProps | LinkCardProps;

const interactiveComponents: HeroUICardProps["as"][] = ["button", "a"];

const isInteractiveComponent = (as: HeroUICardProps["as"]) =>
  interactiveComponents.includes(as);

export function Card({
  as,
  id,
  onPress,
  tour,
  trackEventProps,
  ...props
}: CardProps) {
  const { trackEvent } = useTracking();

  const isButtonCard = as === "button";
  const isLoading = isButtonCard && "isLoading" in props && props.isLoading;
  const isInteractive = isInteractiveComponent(as);

  return (
    <HeroUICard
      {...props}
      id={id}
      {...tour}
      as={as}
      isDisabled={props.isDisabled ?? isLoading}
      isPressable={isInteractive}
      type={isButtonCard ? "button" : undefined}
      onPress={(e) => {
        if (isLoading) {
          return;
        }
        if (isInteractive) {
          trackEvent({
            ...trackEventProps,
            name: generateTrackName({
              id: id,
              trackEventProps: trackEventProps,
              type: isButtonCard ? "Button" : "Link",
            }),
          });
          onPress?.(e);
        }
      }}
    />
  );
}

Card.displayName = "RecareUI.Card";
HeroUICardFooter.displayName = "RecareUI.CardFooter";
HeroUICardHeader.displayName = "RecareUI.CardHeader";
HeroUICardBody.displayName = "RecareUI.CardBody";

export {
  HeroUICardBody as CardBody,
  HeroUICardFooter as CardFooter,
  HeroUICardHeader as CardHeader,
  type CardProps,
};
